"use client";

import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import clsx from "clsx";
import React from "react";
import Ripple from "../Ripple/Ripple";
import "./Tabs.css";

export interface TabsTriggerProps extends BaseTabs.Tab.Props {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

export const TabsTrigger = (props: TabsTriggerProps) => {
  const { asChild, children, ref, className, value, ...otherProps } = props;

  return (
    <BaseTabs.Tab
      ref={ref}
      value={value}
      render={asChild && React.isValidElement(children) ? children : undefined}
      // A tab rendered as a link (`asChild`) has no native button semantics.
      nativeButton={asChild ? false : undefined}
      className={clsx("calmlens-tab", className)}
      {...otherProps}
    >
      {!otherProps.disabled && !asChild && <Ripple />}
      {children}
    </BaseTabs.Tab>
  );
};

export interface TabsContentProps extends BaseTabs.Panel.Props {}

export const TabsContent = (props: TabsContentProps) => {
  const { children, ...otherProps } = props;
  return <BaseTabs.Panel {...otherProps}>{children}</BaseTabs.Panel>;
};

export interface TabsListProps extends BaseTabs.List.Props {
  /** Underline the active tab. On by default. */
  indicatorBar?: boolean;
  /**
   * Tabs share the list's width, centred, and wrap long labels instead of
   * overflowing. Lay the list out yourself (e.g. a grid) to choose the columns.
   */
  fill?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

export const TabsList = (props: TabsListProps) => {
  const { indicatorBar = true, fill = false, ref, className, children, ...otherProps } = props;

  return (
    <BaseTabs.List
      ref={ref}
      className={clsx(
        "calmlens-tabs-list",
        indicatorBar && "calmlens-tabs-indicator",
        fill && "calmlens-tabs-fill",
        className,
      )}
      {...otherProps}
    >
      {children}
    </BaseTabs.List>
  );
};

export interface TabsProps extends Omit<BaseTabs.Root.Props, "value" | "children" | "render"> {
  asChild?: boolean;
  children?: React.ReactNode;
  /** "bare" drops the tab padding and hover fill, for usages that lay tabs out themselves. */
  variant?: "bare" | "normal";
  value?: string | null;
}

const Tabs = (props: TabsProps) => {
  const { variant, value, className, asChild, children, ...otherProps } = props;
  return (
    <BaseTabs.Root
      value={value as any}
      render={asChild && React.isValidElement(children) ? children : undefined}
      className={clsx("calmlens-tabs", `calmlens-tabs-${variant ?? "normal"}`, className)}
      {...otherProps}
    >
      {asChild ? undefined : children}
    </BaseTabs.Root>
  );
};

export default Object.assign(Tabs, {
  Trigger: TabsTrigger,
  Content: TabsContent,
  List: TabsList,
});
